import { useEffect, useState } from 'react'

type UsePdfIndicatorsResult = {
  firstDotActive: boolean
  secondDotActive: boolean
  thirdDotActive: boolean
  fourthDotActive: boolean
}

export const usePdfIndicators = (
  generationProgress: number,
): UsePdfIndicatorsResult => {
  const [animationCycle, setAnimationCycle] = useState(0)

  const ANIMATION_SPEED = 500 // milliseconds per dot

  // Calculate base milestone dots (permanently filled)
  const getMilestoneDots = (progress: number): number => {
    const allDotsCompleted = progress >= 100
    const threeMilestonesCompleted = progress >= 75
    const twoMilestonesCompleted = progress >= 50
    const oneMilestoneCompleted = progress >= 25

    if (allDotsCompleted) return 4
    if (threeMilestonesCompleted) return 3
    if (twoMilestonesCompleted) return 2
    if (oneMilestoneCompleted) return 1
    return 0
  }

  // Calculate cycling animation dots
  const getCyclingDots = (progress: number, cycle: number): number => {
    const milestoneDots = getMilestoneDots(progress)
    const remainingDots = 4 - milestoneDots
    const isCompleted = progress >= 100

    if (isCompleted || remainingDots === 0) {
      return milestoneDots
    }

    // Cycle through remaining dots (0 to remainingDots, then reset)
    const cyclePosition = cycle % (remainingDots + 1)
    return milestoneDots + cyclePosition
  }

  // Get dot states
  const milestoneDots = getMilestoneDots(generationProgress)
  const totalActiveDots = getCyclingDots(generationProgress, animationCycle)
  const isGenerationComplete = generationProgress >= 100

  const firstDotActive = totalActiveDots >= 1
  const secondDotActive = totalActiveDots >= 2
  const thirdDotActive = totalActiveDots >= 3
  const fourthDotActive = totalActiveDots >= 4

  // Animation timer
  useEffect(() => {
    if (isGenerationComplete) {
      return // Stop animation when complete
    }

    const timer = setInterval(() => {
      setAnimationCycle((prev) => prev + 1)
    }, ANIMATION_SPEED)

    return () => clearInterval(timer)
  }, [generationProgress, milestoneDots, ANIMATION_SPEED, isGenerationComplete])

  return {
    firstDotActive,
    secondDotActive,
    thirdDotActive,
    fourthDotActive,
  }
}
